import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useRouter } from 'next/router'
import { useContext, useMemo } from 'react'
import { useAsyncFn } from 'react-use'
import { ContextShell } from '../../../modules/context'

interface UseUnsubscribePlaylist {
  error?: boolean
  loading: boolean
  unsubcribePlaylist(i: { playlistId: string }): void
}

export const useUnsubscribePlaylist = (): UseUnsubscribePlaylist => {
  const { i18n } = useLingui()
  const { pathname, push } = useRouter()
  const {
    user,
    MoisesCLI,
    toast: { add: addToast }
  } = useContext(ContextShell)

  const isDevEnvironment = useMemo(() => {
    if (typeof window === 'undefined') return false
    return (
      process.env.NEXT_PUBLIC_LOCAL_DEV &&
      window?.location?.host === 'dev.moises.ai:3001'
    )
  }, [])

  const [state, unsubcribePlaylist] = useAsyncFn(
    async ({ playlistId }: { playlistId: string }) => {
      let result

      try {
        result = await MoisesCLI.unsubscribeFromPlaylist({ playlistId })

        addToast({
          icon: null,
          closable: true,
          type: 'success',
          description: 'Successfully unsubscribed.'
        })

        if (pathname !== '/library') {
          push(`${isDevEnvironment ? '/studio' : ''}/library`)
        }
      } catch (e: any) {
        addToast({
          icon: null,
          type: 'error',
          closable: true,
          title: i18n._(t`error.deleting.playlist.title`),
          description: i18n
            ._(t`error.create.playlist.description`)
            .replace(/\*/g, '')
        })
      }

      return result
    },
    [i18n, user, MoisesCLI, addToast, pathname, push]
  )

  const { loading, error } = state

  return {
    ...state,
    loading,
    error: !!error,
    unsubcribePlaylist
  }
}
